/**
 * Gets the public environment variable. This is always set to either 'staging' or 'production'.
 * Feature branches are set to 'staging' to improve cache hits.
 */
export function getPublicEnv(): 'staging' | 'production' {
  const env = process.env.NEXT_PUBLIC_ENV ?? process.env.VITE_PUBLIC_ENV
  if (env === 'production') {
    return 'production'
  }
  return 'staging'
}

/**
 * Checks if the current environment is production.
 */
export const isProduction = getPublicEnv() === 'production'

/**
 * Checks if we're on a Creative Fabrica domain (creativefabrica.com or subdomain).
 * This is used for cookie/auth logic since feature branches use vercel.app domains.
 *
 * NOTE: This function can only be used in client-side code (useEffect, event handlers, etc.)
 * as it accesses window.location.
 */
export function isOnCFDomain(): boolean {
  if (typeof window === 'undefined') {
    throw new Error('isOnCFDomain() can only be called in client-side code')
  }
  return Boolean(window.location.hostname.match(/\.creativefabrica\.com|creativefabrica\.com/))
}

/**
 * Determines the authentication strategy based on the current hostname.
 *
 * - If on creativefabrica.com or subdomain: use cookie-based auth (cookies work)
 * - If on other domains (e.g., vercel.app): use token-based auth (localStorage)
 *
 * NOTE: This function can only be used in client-side code as it accesses window.location.
 */
export function canUseCookieAuth(): boolean {
  if (typeof window === 'undefined') {
    throw new Error('canUseCookieAuth() can only be called in client-side code')
  }
  return isOnCFDomain()
}

/**
 * Gets the cookie domain to use, or undefined if cookies cannot be used.
 *
 * @param cookieDomain - The cookie domain from env vars (e.g., '.creativefabrica.com')
 * @returns The cookie domain to use, or undefined if we're not on a CF domain
 */
export function getCookieDomain(cookieDomain: string): string | undefined {
  if (typeof window === 'undefined') {
    throw new Error('getCookieDomain() can only be called in client-side code')
  }
  return canUseCookieAuth() ? cookieDomain : undefined
}

export const enableSentry =
  process.env.NODE_ENV === 'production' && process.env.TEST_BROWSER !== '1'
